import { MouseEventHandler, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { APP_CLASSES_SHOW_ASSISTANT } from "consts";
import { TooltipData } from "types";
import { TableRoadClasses } from "components/TableRoadClasses";
import getStyles from "./styles";

const ClassInformation = ({ tooltips }: { tooltips: TooltipData | null }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).ClassInformation, [theme]);
  const [showRequirements, setShowRequirements] = useState({ show: false, roadClass: "" });

  const handleOpenMClasses = useCallback(() => {
    setShowRequirements({ show: true, roadClass: "m" });
  }, []);

  const handleOpenCClasses = useCallback(() => {
    setShowRequirements({ show: true, roadClass: "c" });
  }, []);

  const handleOpenPClasses = useCallback(() => {
    setShowRequirements({ show: true, roadClass: "p" });
  }, []);

  const handleCloseRequirements = useCallback(() => {
    setShowRequirements({ show: false, roadClass: "" });
  }, []);

  const handleCloseClick: MouseEventHandler<HTMLDivElement> = useCallback(() => {
    handleCloseRequirements();
  }, [handleCloseRequirements]);

  return (
    <Stack direction="column">
      <Alert variant="outlined" severity="info" sx={styles.alert as SxProps<Theme>}>
        <Typography variant="body2" sx={styles.text}>
          {t("What are")}{" "}
          <Typography variant="body2" onClick={handleOpenMClasses} sx={styles.link}>
            M-{t("Classes")}
          </Typography>
          ,{" "}
          <Typography variant="body2" onClick={handleOpenCClasses} sx={styles.link}>
            C-{t("Classes")}
          </Typography>{" "}
          {t("and")}{" "}
          <Typography variant="body2" onClick={handleOpenPClasses} sx={styles.link}>
            P-{t("Classes")}
          </Typography>
          ? {t("How should they be applied?")}
        </Typography>
      </Alert>

      {showRequirements.show && (
        <Alert
          variant="outlined"
          severity="info"
          icon={false}
          slots={{ closeIcon: CancelIcon }}
          slotProps={{ closeIcon: { sx: { fill: "var(--color-blue)" } } }}
          onClose={handleCloseRequirements}
          sx={styles.alert as SxProps<Theme>}
        >
          <TableRoadClasses roadClass={showRequirements.roadClass} tooltips={tooltips} />

          <Stack direction="row" justifyContent="flex-end" onClick={handleCloseClick} sx={styles.close}>
            <Button variant="text" size="small">
              {t("Close")}
            </Button>
          </Stack>
        </Alert>
      )}

      {APP_CLASSES_SHOW_ASSISTANT && (
        <Alert variant="outlined" severity="info" sx={styles.alert as SxProps<Theme>}>
          <Box
            component="a"
            href="https://www.trilux.com/de/service/tools/beleuchtungsklassen-assistent-13201/assistent/"
            target="_blank"
            rel="noopener noreferrer"
            sx={styles.link}
          >
            {t("Here you can determine your lighting class in accordance with DIN 13201.")}
          </Box>
        </Alert>
      )}
    </Stack>
  );
};

export default ClassInformation;
